import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, CalendarIcon, Wand2, Repeat, Printer, Pencil, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { TurnoEditDialog } from "@/components/mansioni/TurnoEditDialog";
import { BulkDeleteDialog } from "@/components/mansioni/BulkDeleteDialog";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/format";
import StudentPicker from "@/components/StudentPicker";
import { ConflictPanel } from "@/components/ConflictPanel";

const DAYS = [["1","Lun"],["2","Mar"],["3","Mer"],["4","Gio"],["5","Ven"],["6","Sab"],["0","Dom"]];

const isoLocal = (d) => d.toLocaleDateString("en-CA");
const weekOf = (base) => {
  const d = new Date(base); d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const end = new Date(d); end.setDate(d.getDate() + 6);
  return { since: isoLocal(d), until: isoLocal(end) };
};
const shiftWeek = (since, delta) => { const d = new Date(`${since}T12:00:00`); d.setDate(d.getDate() + delta * 7); return weekOf(d); };

function RangeFilter({ range, onApply, count, days }) {
  const [draft, setDraft] = useState(range);
  React.useEffect(() => { setDraft(range); }, [range]);
  const valid = draft.since && draft.until && draft.since <= draft.until;
  return (
    <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="turni-range-filter">
      <div><Label className="text-xs">Da</Label><Input type="date" value={draft.since} onChange={(e) => setDraft({ ...draft, since: e.target.value })} className="mt-1 w-[160px]" data-testid="turni-since"/></div>
      <div><Label className="text-xs">A</Label><Input type="date" value={draft.until} onChange={(e) => setDraft({ ...draft, until: e.target.value })} className="mt-1 w-[160px]" data-testid="turni-until"/></div>
      <Button onClick={() => onApply(draft)} disabled={!valid} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" data-testid="turni-filter-btn"><Filter size={14} className="mr-1.5"/> Filtra</Button>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" className="rounded-full h-9 w-9 p-0" onClick={() => onApply(shiftWeek(range.since, -1))} data-testid="turni-prev-week"><ChevronLeft size={15}/></Button>
        <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => onApply(weekOf(new Date()))} data-testid="turni-this-week">Questa settimana</Button>
        <Button variant="outline" size="sm" className="rounded-full h-9 w-9 p-0" onClick={() => onApply(shiftWeek(range.since, 1))} data-testid="turni-next-week"><ChevronRight size={15}/></Button>
      </div>
      <div className="text-xs text-stone-500 pb-2" data-testid="turni-count">{count} turni nel periodo · {days} giorni con attività</div>
    </div>
  );
}

export default function Turni() {
  const qc = useQueryClient();
  const [range, setRange] = useState(() => weekOf(new Date()));
  const { since, until } = range;
  const [open, setOpen] = useState(false);
  const [conflictWarn, setConflictWarn] = useState(null);
  const [form, setForm] = useState(getEmptyForm());
  const [pdfLoading, setPdfLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await api.get("/mansioni-area/turni-pdf", { params: { since, until }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `planning_mansioni_${since}_${until}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error("Impossibile creare il PDF (intervallo massimo 3 mesi)");
    } finally {
      setPdfLoading(false);
    }
  };

  function getEmptyForm() {
    return {
      student_id: "", mansione_id: "",
      date: new Date().toLocaleDateString("en-CA"),
      start_time: "", end_time: "", note: "",
      recurrence: "none", recurrence_days: [], recurrence_end_date: "",
      force: false,
    };
  }

  const { data: turni = [] } = useQuery({
    queryKey: ["turni", { since, until }],
    queryFn: async () => (await api.get("/mansioni-area/turni", { params: { since, until } })).data,
  });
  const { data: mansioni = [] } = useQuery({
    queryKey: ["mansioni-active"],
    queryFn: async () => (await api.get("/mansioni-area/mansioni", { params: { only_active: true } })).data,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-list", "attivo", "resid"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo", track_type: "residenziale" } })).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/mansioni-area/turni", form)).data,
    onSuccess: (d) => {
      toast.success(`${d.created} turno/i creato/i`);
      qc.invalidateQueries({ queryKey: ["turni"] });
      setOpen(false); setForm(getEmptyForm()); setConflictWarn(null);
    },
    onError: (e) => {
      const raw = e.response?.data?.detail_raw;
      if (e.response?.status === 409 && raw?.conflicts) {
        setConflictWarn(raw);
      } else {
        toast.error(e.response?.data?.detail || "Errore creazione turno");
      }
    },
  });

  const delMut = useMutation({
    mutationFn: async ({ id, scope }) => (await api.delete(`/mansioni-area/turni/${id}`, { params: { scope } })).data,
    onSuccess: (d) => { toast.success(`${d.deleted} turno/i eliminato/i`); qc.invalidateQueries({ queryKey: ["turni"] }); },
  });

  const grouped = useMemo(() => {
    const map = {};
    turni.forEach((t) => { (map[t.date] ||= []).push(t); });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b));
  }, [turni]);

  const toggleDay = (d) => {
    const set = new Set(form.recurrence_days.map(String));
    set.has(d) ? set.delete(d) : set.add(d);
    setForm({...form, recurrence_days: [...set].map(Number)});
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Turni & Mansioni"
        subtitle="Assegna studenti a mansioni ricorrenti o singole. Verifica automatica dei conflitti con impegni esterni."
        actions={
          <>
          <Button variant="outline" className="rounded-full text-red-700 border-red-200 hover:bg-red-50" onClick={() => setBulkOpen(true)} data-testid="turni-bulk-delete">
            <Trash2 size={14} className="mr-1.5"/> Elimina in blocco
          </Button>
          <Button variant="outline" className="rounded-full" disabled={pdfLoading} onClick={downloadPdf} data-testid="turni-pdf">
            <Printer size={14} className="mr-1.5"/> {pdfLoading ? "Creo PDF…" : "Stampa planning PDF"}
          </Button>
          <Link to="/mansioni/turni/genera">
            <Button variant="outline" className="rounded-full" data-testid="turni-generate-link"><Wand2 size={14} className="mr-1.5"/> Genera automaticamente</Button>
          </Link>
          <Button data-testid="turno-add-btn" onClick={() => { setForm(getEmptyForm()); setConflictWarn(null); setOpen(true); }} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
            <Plus size={14} className="mr-1.5"/> Nuovo turno
          </Button>
          </>
        }
      />

      <RangeFilter range={range} onApply={setRange} count={turni.length} days={grouped.length}/>

      <div className="space-y-4">
        {grouped.length === 0 && <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessun turno nel range selezionato.</Card>}
        {grouped.map(([day, items]) => (
          <Card key={day} className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
            <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 flex items-center gap-2 text-sm font-medium">
              <CalendarIcon size={14}/> {fmtDate(day)}
              <span className="text-xs text-stone-500 font-normal">· {items.length} turno/i</span>
            </div>
            <div className="divide-y divide-stone-100">
              {items.map((t) => (
                <div key={t.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`turno-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: t.mansione_color || "#556B2F" }}/>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.mansione_name}</div>
                      <div className="text-[11px] text-stone-500 truncate">
                        {t.student_name} · {t.start_time || "tutto il giorno"}{t.end_time ? ` – ${t.end_time}` : ""}
                        {t.note && ` · ${t.note}`}
                      </div>
                    </div>
                    {t.override_conflict && <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" title="Salvato con override conflitti"/>}
                    {t.substitute_for && <Repeat size={13} className="text-amber-600 flex-shrink-0" title="Sostituzione per impegno esterno del titolare"/>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(t)} data-testid={`turno-edit-${t.id}`}>
                      <Pencil size={13}/>
                    </Button>
                    {t.series_id && (
                      <Button variant="ghost" size="sm" className="text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm("Eliminare TUTTA la serie ricorrente?")) delMut.mutate({ id: t.id, scope: "series" }); }}>
                        Elimina serie
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => { if (confirm("Eliminare questo turno?")) delMut.mutate({ id: t.id, scope: "single" }); }} data-testid={`turno-del-${t.id}`}>
                      <Trash2 size={13}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <TurnoEditDialog turno={editing} onClose={() => setEditing(null)} students={students} mansioni={mansioni}/>
      <BulkDeleteDialog open={bulkOpen} onClose={() => setBulkOpen(false)} mansioni={mansioni} initialSince={since} initialUntil={until}/>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConflictWarn(null); }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo turno</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Studente</Label>
                <StudentPicker students={students} value={form.student_id} onChange={(id) => setForm({...form, student_id: id})} testId="turno-student"/>
              </div>
              <div>
                <Label>Mansione</Label>
                <Select value={form.mansione_id} onValueChange={(v) => setForm({...form, mansione_id: v})}>
                  <SelectTrigger data-testid="turno-mansione" className="mt-1.5"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                  <SelectContent>
                    {mansioni.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} data-testid="turno-date" className="mt-1.5"/></div>
              <div><Label>Ora inizio</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} data-testid="turno-start" className="mt-1.5"/></div>
              <div><Label>Ora fine</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className="mt-1.5"/></div>
            </div>

            <div>
              <Label>Ricorrenza</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm({...form, recurrence: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna (evento singolo)</SelectItem>
                  <SelectItem value="daily">Giornaliera</SelectItem>
                  <SelectItem value="weekly">Settimanale (giorni scelti)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.recurrence === "weekly" && (
              <div>
                <Label>Giorni settimana</Label>
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {DAYS.map(([d, lbl]) => (
                    <button key={d} type="button" onClick={() => toggleDay(d)}
                      className={`px-2.5 py-1 rounded-full text-xs border ${form.recurrence_days.map(String).includes(d) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {form.recurrence !== "none" && (
              <div>
                <Label>Fine ricorrenza</Label>
                <Input type="date" value={form.recurrence_end_date} onChange={(e) => setForm({...form, recurrence_end_date: e.target.value})} className="mt-1.5"/>
              </div>
            )}

            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} rows={2} className="mt-1.5"/></div>

            <ConflictPanel warn={conflictWarn} force={form.force} onForceChange={(v) => setForm({...form, force: v})} testIdPrefix="turno"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="turno-save" className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!form.student_id || !form.mansione_id || createMut.isPending || (conflictWarn && !form.force)} onClick={() => createMut.mutate()}>
              {createMut.isPending ? "Salvataggio…" : (form.force && conflictWarn ? "Salva (con override)" : "Crea turno")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
